import React from 'react';
import { Users, Target, Award, Globe, TrendingUp, Shield } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const About = () => {
  const values = [
    {
      icon: Target,
      title: 'Innovation-First',
      description: 'We leverage cutting-edge technologies to solve complex business challenges and drive digital transformation.'
    },
    {
      icon: Users,
      title: 'Expert Team',
      description: 'Our skilled professionals bring deep expertise across multiple domains and technologies.'
    },
    {
      icon: Globe,
      title: 'Global Reach',
      description: 'Serving clients worldwide with 24/7 support and localized expertise across multiple time zones.'
    },
    {
      icon: Shield,
      title: 'Reliability',
      description: 'Committed to delivering high-quality solutions with robust security and compliance standards.'
    }
  ];

  const milestones = [
    { year: '2015', milestone: 'Company Founded', description: 'Tripod Technologies established in Nagpur' },
    { year: '2017', milestone: 'Global Expansion', description: 'Extended services to US and UK markets' },
    { year: '2023', milestone: 'AI Innovation', description: 'Launched AI and machine learning practice' },
    { year: '2024', milestone: 'HealthTech Focus', description: 'Specialized solutions for healthcare industry' },
    { year: '2025', milestone: 'Algo Trading', description: 'Advanced algorithmic trading applications' }
  ];

  const leadership = [
    {
      name: 'Chintan Vora',
      position: 'CEO & Founder',
      experience: '18+ years in IT leadership',
      expertise: 'Strategic Planning, Project Management, Business Development, Finance Management',
      image: '/team/chintan.jpeg'
    },
    {
      name: 'Shikha Gupta',
      position: 'VP of HR & Operations',
      experience: '10+ years in HR & operations',
      expertise: 'HR & Operations',
      image: '/team/shikha.jpeg'
    },
    {
      name: 'Minal Watmode',
      position: 'Head of Technology and Digital Marketing',
      experience: '',
      expertise: '',
      image: ''
    },
    {
      name: 'Kush Jais',
      position: 'Head of Recruiting and Sales',
      experience: '',
      expertise: '',
      image: ''
    }
  ];

  return (
    <section id="about" className="py-20 bg-white relative overflow-hidden">
      {/* Tech Background Pattern */}
      <div className="absolute inset-0 opacity-8">
        <div className="absolute top-10 left-10 w-64 h-64 border border-purple-200 rounded-full animate-spin-slow"></div>
        <div className="absolute bottom-10 right-10 w-48 h-48 border border-cyan-200 rounded-lg rotate-45 animate-pulse"></div>
        <div className="absolute top-1/2 right-1/4 w-32 h-32 border border-purple-300/50 rounded-full"></div>
        {/* Tech grid pattern */}
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(139, 69, 193, 0.02) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(139, 69, 193, 0.02) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }}></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <h2 className="text-3xl lg:text-5xl font-bold mb-6">
            <span className="text-transparent bg-gradient-to-r from-purple-600 via-cyan-500 to-violet-500 bg-clip-text animate-pulse">About Tripod Technologies</span>
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed mb-8">
            Founded with a vision to bridge the gap between innovative technology and business success, 
            Tripod Technologies has emerged as a trusted partner for organizations worldwide seeking 
            digital transformation and technical excellence.
          </p>
          <div className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto">
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600 mb-2">10+</div>
              <div className="text-gray-600">Years of Excellence</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600 mb-2">85+</div>
              <div className="text-gray-600">Projects Delivered</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600 mb-2">4</div>
              <div className="text-gray-600">Global Regions</div>
            </div>
          </div>
        </div>

        {/* Mission & Vision */}
        <div className="grid lg:grid-cols-2 gap-12 mb-20">
          <Card className="shadow-lg border-0 hover:shadow-xl transition-shadow duration-300 bg-gradient-to-br from-purple-50 to-violet-50">
            <CardContent className="p-8">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                  <Target className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="text-2xl font-bold text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text">Our Mission</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                To empower businesses globally through innovative technology solutions, expert consultation, 
                and reliable partnerships that drive sustainable growth and competitive advantage in the digital age.
              </p>
            </CardContent>
          </Card>

          <Card className="shadow-lg border-0 hover:shadow-xl transition-shadow duration-300 bg-gradient-to-br from-violet-50 to-purple-50">
            <CardContent className="p-8">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-violet-100 rounded-lg flex items-center justify-center mr-4">
                  <TrendingUp className="w-6 h-6 text-violet-600" />
                </div>
                <h3 className="text-2xl font-bold text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text">Our Vision</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                To become the world's most trusted technology partner, recognized for our innovation, 
                quality, and ability to transform businesses through cutting-edge solutions and exceptional service.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Core Values */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h3 className="text-2xl font-bold text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text mb-4">Our Core Values</h3>
            <p className="text-gray-600 max-w-2xl mx-auto">
              The principles that guide our work and define our company culture
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <Card key={index} className="text-center hover:shadow-xl hover:shadow-cyan-500/20 transition-all duration-300 border-0 shadow-lg hover:-translate-y-2 transform-gpu bg-gradient-to-br from-white via-purple-50/30 to-cyan-50/30 border border-cyan-200/50">
                  <CardContent className="p-6">
                    <div className="w-16 h-16 bg-gradient-to-r from-purple-100 via-cyan-50 to-violet-50 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-cyan-500/20 border border-cyan-200/50">
                      <IconComponent className="w-8 h-8 text-purple-600 hover:text-cyan-600 transition-colors duration-300" />
                    </div>
                    <h4 className="font-bold text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text mb-3">{value.title}</h4>
                    <p className="text-gray-600 text-sm leading-relaxed">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Company Timeline */}
        <div className="mb-20 overflow-visible">
          <div className="text-center mb-12">
            <h3 className="text-2xl font-bold text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text mb-4">Our Journey</h3>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Milestones that mark our growth and evolution as a technology leader
            </p>
          </div>
          <div className="relative max-w-6xl mx-auto">
            <div className="space-y-12 px-4">
              {milestones.map((item, index) => (
                <div key={index} className="relative flex items-center justify-center">
                  {/* Left side for even indices, right side for odd indices */}
                  <div className={`w-full max-w-md ${index % 2 === 0 ? 'mr-auto pr-12' : 'ml-auto pl-12'}`}>
                    <Card className="shadow-lg border-0 hover:shadow-xl hover:shadow-cyan-500/20 transition-all duration-300 bg-gradient-to-br from-white via-purple-50/20 to-cyan-50/20 border border-cyan-200/50 w-full">
                      <CardContent className="p-6">
                        <div className={`${index % 2 === 0 ? 'text-right' : 'text-left'}`}>
                          <div className="text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text font-bold text-lg mb-2">{item.year}</div>
                          <h4 className="font-bold text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text mb-2">{item.milestone}</h4>
                          <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Leadership Team */}
        <div className="text-center">
          <h3 className="text-2xl font-bold text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text mb-4">Leadership Team</h3>
          <p className="text-gray-600 max-w-2xl mx-auto mb-12">
            Experienced leaders driving innovation and excellence in technology solutions
          </p>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {leadership.map((leader, index) => (
              <Card key={index} className="shadow-lg border-0 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 transform-gpu perspective-1000 hover:rotate-y-5 bg-white/90 backdrop-blur-sm border border-cyan-200/50">
                <CardContent className="p-8 text-center">
                  <div className="w-40 h-40 mx-auto mb-6 rounded-full overflow-hidden border-4 border-gradient-to-r from-purple-400 to-cyan-400 shadow-xl shadow-cyan-500/30 relative">
                    {leader.image ? (
                      <img 
                        src={leader.image} 
                        alt={leader.name}
                        className={`w-full h-full ${leader.name === 'Chintan Vora' ? 'object-cover object-top scale-110' : 'object-cover object-center'} filter brightness-105 contrast-105`}
                        style={{
                          objectPosition: leader.name === 'Chintan Vora' ? '50% 20%' : '50% 50%'
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-r from-purple-600 to-cyan-500 text-white text-4xl font-bold">
                        {String(leader.name || '')
                          .split(' ')
                          .filter(Boolean)
                          .slice(0, 2)
                          .map(s => s[0])
                          .join('')
                          .toUpperCase()}
                      </div>
                    )}
                    <div className="absolute inset-0 border-4 border-transparent bg-gradient-to-r from-purple-400 via-cyan-400 to-purple-400 rounded-full animate-spin-slow opacity-30"></div>
                  </div>
                  <h4 className="font-bold text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text text-xl mb-2 drop-shadow-sm">{leader.name}</h4>
                  <div className="text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text font-medium mb-2 text-lg">{leader.position}</div>
                  {!!leader.experience && <div className="text-gray-600 text-sm mb-3">{leader.experience}</div>}
                  {!!leader.expertise && <div className="text-gray-700 text-sm">{leader.expertise}</div>}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
